export const AVAILABLE_LOCALES = ["en", "pl"] as const

export type AppLocale = (typeof AVAILABLE_LOCALES)[number]

export const DEFAULT_LOCALE: AppLocale = "en"
export const LOCALE_STORAGE_KEY = "tabb:locale"

const COUNTRY_TO_LOCALE: Record<string, AppLocale> = {
  PL: "pl",
}

export const normalizeLocale = (locale?: string | null): AppLocale | null => {
  if (!locale) return null

  const normalized = locale.trim().replace(/_/g, "-").toLowerCase()
  if (!normalized) return null

  const language = normalized.split("-")[0]
  return AVAILABLE_LOCALES.includes(language as AppLocale) ? (language as AppLocale) : null
}

export const getLocaleForCountry = (country?: string | null): AppLocale => {
  if (!country) return DEFAULT_LOCALE

  return normalizeLocale(COUNTRY_TO_LOCALE[country.toUpperCase()]) || DEFAULT_LOCALE
}

export const detectBrowserLocale = (): AppLocale | null => {
  if (typeof navigator === "undefined") return null

  const candidates = [...(navigator.languages || []), navigator.language]

  for (const candidate of candidates) {
    const locale = normalizeLocale(candidate)
    if (locale) return locale
  }

  return null
}

export const loadStoredLocale = (): AppLocale | null => {
  if (typeof window === "undefined") return null

  const locale = normalizeLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY))

  if (!locale) {
    window.localStorage.removeItem(LOCALE_STORAGE_KEY)
  }

  return locale
}

export const persistLocale = (locale: string): AppLocale => {
  const normalized = normalizeLocale(locale) || DEFAULT_LOCALE

  if (typeof window !== "undefined") {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, normalized)
  }

  return normalized
}

export const resolveInitialLocale = ({
  storedLocale,
  browserLocale,
  venueLocale,
  country,
  fallbackLocale,
}: {
  storedLocale?: string | null
  browserLocale?: string | null
  venueLocale?: string | null
  country?: string | null
  fallbackLocale?: string | null
}): AppLocale => {
  const countryLocale = country ? getLocaleForCountry(country) : null

  return (
    normalizeLocale(storedLocale) ||
    normalizeLocale(browserLocale) ||
    normalizeLocale(venueLocale) ||
    countryLocale ||
    normalizeLocale(fallbackLocale) ||
    DEFAULT_LOCALE
  )
}
